import { Avatar, Divider, Tooltip } from 'antd';
import {
  SupplementalKnowledgeValue,
  updateSupplementKnowledge,
} from 'services/api/supplementKnowledge';
import { getTranslateContent } from '../utils';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { eventBus } from 'services/eventBus';
import { useTranslate } from '@tolgee/react';
import { useLayoutEffect } from 'react';
import { Section } from './Section';
import { KnowledgeSelector } from 'components/Dialogue/components/QAPair/KnowledgeSelector';
import { getQAQueries } from 'hooks/business/im/useQAPairTrigger';
import { useQuery } from '@tanstack/react-query';
import { queries } from 'services/queries';

export interface PureBuyerQuestionProps {
  extra?: React.ReactNode;
  buyerNick: string;
  question: string;
  questionTranslate?: string;
  onLocate?: () => void;
}

export const PureBuyerQuestionContent = (props: PureBuyerQuestionProps) => {
  const { question, questionTranslate, buyerNick, onLocate } = props;
  const systemLanguage = useSystemLanguageValue();
  const { t } = useTranslate();

  const { data } = useQuery({
    enabled: !!question && !questionTranslate,
    ...queries.translates.overseaTranslate(question, systemLanguage, 'supplementKnowledge'),
  });

  const translateContent = questionTranslate || data;

  const content = (
    <div
      style={onLocate ? { cursor: 'pointer' } : undefined}
      className="bg-[#DEF6F6] px-[10px] py-2 rounded-sm flex-1"
      onClick={onLocate}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs text-[#515359]">{question}</span>
      </div>
      {translateContent && (
        <>
          <Divider className="!my-2" />
          <span className="text-xs text-[#84868C]">{translateContent}</span>
        </>
      )}
    </div>
  );
  return (
    <div className="flex gap-2">
      <Avatar className="!bg-[#E9386F]" shape="square" size={32}>
        {buyerNick?.slice(0, 1).toUpperCase()}
      </Avatar>
      {!!onLocate ? (
        <Tooltip title={t('定位至买家问题')} placement="bottom">
          {content}
        </Tooltip>
      ) : (
        content
      )}
    </div>
  );
};

export const PureBuyerQuestion = (props: PureBuyerQuestionProps) => {
  const { extra } = props;
  const { t } = useTranslate();

  return (
    <Section title={t('买家问题小结')} extra={extra}>
      <PureBuyerQuestionContent {...props} />
    </Section>
  );
};

interface BuyerQuestionProps {
  citeMode?: boolean;
  conversation: Conversation.Conversation;
  knowledgeId?: number;
  supplementalKnowledgeValue: SupplementalKnowledgeValue;
  showKnowledgeSelector?: boolean;
  onRefresh?: () => void;
  autoLocate?: boolean;
}

export const BuyerQuestion = (props: BuyerQuestionProps) => {
  const {
    citeMode,
    conversation,
    knowledgeId,
    supplementalKnowledgeValue,
    showKnowledgeSelector,
    autoLocate = true,
    onRefresh,
  } = props;
  const { buyerNick, rewriteQuestion, rewriteQuestionTranslateMap, question } =
    supplementalKnowledgeValue;
  const { topBarInfo } = conversation;

  const language = useSystemLanguageValue();
  const qTranslate = getTranslateContent(
    language,
    rewriteQuestionTranslateMap || question?.translateMap,
  );

  const locate = citeMode
    ? undefined
    : () => {
        if (!question?.messageId) return;
        eventBus.emit('SCROLL_TO_MSG_BY_ID', question.messageId);
      };

  const { validQueries } = getQAQueries(topBarInfo?.knowledgeReplies);

  const handleQuestionChange = async (knowledgeQuery: Conversation.Message) => {
    if (!knowledgeId) return;
    const { msgId, textContent } = knowledgeQuery;
    await updateSupplementKnowledge({
      id: knowledgeId,
      supplementalKnowledgeValue: {
        question: {
          messageId: msgId,
          message: textContent?.text || '',
        },
        rewriteQuestion: textContent?.text || '',
      },
    });
    onRefresh?.();
  };

  useLayoutEffect(() => {
    if (!autoLocate) return;

    const timer = setTimeout(() => {
      locate?.();
    }, 1000);
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLocate]);

  return (
    <PureBuyerQuestion
      buyerNick={buyerNick}
      question={rewriteQuestion || question?.message || ''}
      questionTranslate={qTranslate}
      extra={
        showKnowledgeSelector && validQueries.length > 1 ? (
          <KnowledgeSelector
            value={`${question?.messageId}`}
            knowledgeSelections={validQueries}
            onChange={handleQuestionChange}
          />
        ) : null
      }
      onLocate={locate}
    />
  );
};
